import type {
  BuildingAnalyticBRep,
  BuildingAnalyticEntityStyle,
  BuildingAnalyticExtrusion,
  BuildingAnalyticFace,
  BuildingAnalyticLineSegment,
  BuildingAnalyticPolyLine,
} from "building-types/buildingAnalyticEntityTypes";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { inchesCubedNew, inchesSquaredNew, slopeNew, worldInchesNew } from "common/utils/nominalNumbers";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { BRep, Extrusion } from "geom/brep";
import { brepAABB3, faceAABB3, faceArea, faceProjectCopyOnPlane, type Face } from "geom/brep";
import { extrusionAABB3ByBRep, extrusionToBRep } from "geom/extrude";
import type { Line2Segment } from "geom/line2";
import { line2Len } from "geom/line2";
import { line3Len, line3Slope, type Line3Segment } from "geom/line3";
import { planeNewXY, planeProjectPolyLine3, planeSlope } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { calculateMinAreaAABB2, polyLine2AABB2, polyLine2Length } from "geom/polyLine2";
import { polyLine3AABB3, polyLine3Length, type PolyLine3 } from "geom/polyLine3";
import type { PolyLineEdgeType } from "geom/polyLineTypes";
import { unionCoplanarFaces } from "geom/unionFaces";
import type { UVTransformation } from "geom/uv";
import { type Vec2 } from "geom/vec2";
import { vec3Len } from "geom/vec3";

export function buildingAnalyticFaceNew(
  entityType: BuildingAnalyticEntityType,
  name: string,
  parents: DocEntityHandle[],
  face: Face,
  materialSrc: MaterialSrc | undefined,
  materialTransformation: UVTransformation | undefined,
  id: string,
  tags: BuildingGeomTag[] = [],
  style?: BuildingAnalyticEntityStyle,
  loopEdgeTypes?: readonly (readonly PolyLineEdgeType[])[],
): BuildingAnalyticFace {
  const faceInPlan = faceProjectCopyOnPlane(face, planeNewXY());
  const aabb = faceAABB3(face);
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType,
      id,
    },
  };
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, aabb);
  for (const parent of parents) {
    BuildingGenerationContext.indexRelate(parent, "HOSTS", entityNode);
  }
  return {
    name,
    id,
    parents,
    face,
    materialSrc,
    materialTransformation,
    loopEdgeTypes,
    slope: planeSlope(face.plane),
    area: inchesSquaredNew(faceArea(face)),
    areaInPlan: inchesSquaredNew(faceArea(faceInPlan)),
    perimeter: worldInchesNew(polyLine2Length(face.loops[0])),
    faceInPlan,
    entityType,
    geometryType: "FACE",
    tags,
    docEntityHandle,
    pickHandle: docEntityHandle,
    style,
  };
}

export function buildingAnalyticLineSegmentNew(
  entityType: BuildingAnalyticEntityType,
  name: string,
  parents: DocEntityHandle[],
  lineSegment: Line3Segment,
  id: string,
  tags: BuildingGeomTag[] = [],
  style?: BuildingAnalyticEntityStyle,
): BuildingAnalyticLineSegment {
  const lineSegmentInPlan = planeProjectPolyLine3(
    planeNewXY(),
    lineSegment as unknown as PolyLine3,
  ) as unknown as Line2Segment;
  const aabb = polyLine3AABB3(lineSegment);
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType,
      id,
    },
  };
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, aabb);
  for (const parent of parents) {
    BuildingGenerationContext.indexRelate(parent, "HOSTS", entityNode);
  }
  return {
    name,
    parents,
    lineSegment,
    lineSegmentInPlan,
    slope: slopeNew(Math.abs(line3Slope(...lineSegment))), // negative slope is meaningless for analytic lines
    length: worldInchesNew(line3Len(...lineSegment)),
    lengthInPlan: worldInchesNew(line2Len(...lineSegmentInPlan)),
    entityType,
    geometryType: "LINE_SEGMENT",
    id,
    tags,
    docEntityHandle,
    pickHandle: docEntityHandle,
    style,
  };
}

export function buildingAnalyticPolyLineNew(
  entityType: BuildingAnalyticEntityType,
  name: string,
  parents: DocEntityHandle[],
  polyLine: PolyLine3,
  id: string,
  tags: BuildingGeomTag[] = [],
  style?: BuildingAnalyticEntityStyle,
): BuildingAnalyticPolyLine {
  const polyLineInPlan = planeProjectPolyLine3(planeNewXY(), polyLine);
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType,
      id,
    },
  };
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, polyLine3AABB3(polyLine));
  for (const parent of parents) {
    BuildingGenerationContext.indexRelate(parent, "HOSTS", entityNode);
  }
  return {
    name,
    parents,
    polyLine,
    polyLineInPlan,
    length: worldInchesNew(polyLine3Length(polyLine)),
    lengthInPlan: worldInchesNew(polyLine2Length(polyLineInPlan)),
    entityType,
    geometryType: "POLYLINE",
    id,
    tags,
    docEntityHandle,
    pickHandle: docEntityHandle,
    style,
  };
}

/**
 * Plan footprint length/width in world inches (XY projection), matching analytic extrusion
 * takeoff: min-area AABB of the projected profile, then alignment to strip direction when given.
 *
 * @param profileInPlan - If set, reused instead of projecting again (avoids a second extrusionToBRep).
 */
export function extrusionPlanFootprintDimsForLenDir(
  extrusion: Extrusion,
  lenDir?: Vec2,
  profileInPlan?: Face,
): { width: number; length: number } {
  const projected =
    profileInPlan !== undefined ? profileInPlan : extrusionProfileProjectedOnPlane(extrusion, planeNewXY());
  if (!projected?.loops[0]) {
    return { width: 0, length: 0 };
  }
  const aabb2Rotated = calculateMinAreaAABB2(projected.loops[0]);
  const length = aabb2Width(aabb2Rotated);
  const width = aabb2Height(aabb2Rotated);
  let correctedLength = length;
  let correctedWidth = width;
  let isLengthLonger: boolean = true;

  if (width > length) {
    correctedLength = width;
    correctedWidth = length;
    isLengthLonger = false;
  }

  if (lenDir !== undefined) {
    const lenDirX = lenDir[0];
    const lenDirY = lenDir[1];
    const [aabb2DirX, aabb2DirY] = [Math.cos(-aabb2Rotated.rot), Math.sin(-aabb2Rotated.rot)];
    const angle1 = Math.atan(lenDirY / lenDirX);
    const angle2 = Math.atan(aabb2DirY / aabb2DirX);
    const absAngleDiff = Math.abs(angle1 - angle2);

    if (absAngleDiff <= Math.PI / 4) {
      if (isLengthLonger) {
        correctedWidth = width;
        correctedLength = length;
      }
    } else {
      if (!isLengthLonger) {
        correctedWidth = length;
        correctedLength = width;
      }
    }
  }

  return { width: correctedWidth, length: correctedLength };
}

/**
 * Finds the width and height of the extrusion profile in its local coordinate system.
 */
export function extrusionProfileLocalDims(extrusion: Extrusion): { width: number; length: number } {
  const loop = extrusion.profile.loops[0];
  if (!loop) {
    return { width: 0, length: 0 };
  }

  const aabb = polyLine2AABB2(loop);
  return { width: aabb2Width(aabb), length: aabb2Height(aabb) };
}

export function buildingAnalyticExtrusionNew(
  entityType: BuildingAnalyticEntityType,
  name: string,
  parents: DocEntityHandle[],
  extrusion: Extrusion,
  materialSrc: MaterialSrc | undefined,
  id: string,
  tags: BuildingGeomTag[] = [],
  style?: BuildingAnalyticEntityStyle,
  type?: string,
  lenDir?: Vec2,
  useProfileLocalDimensions?: boolean,
): BuildingAnalyticExtrusion {
  const { profile, dirX, dirY, dirZ } = extrusion;
  const area = inchesSquaredNew(faceArea(profile));
  const thickness = worldInchesNew(vec3Len(dirX, dirY, dirZ));
  const volume = inchesCubedNew(area * thickness);
  const profileInPlan = extrusionProfileProjectedOnPlane(extrusion, planeNewXY());
  const profileInPlanArea = inchesSquaredNew(faceArea(profileInPlan));
  const { width: correctedWidth, length: correctedLength } = useProfileLocalDimensions
    ? extrusionProfileLocalDims(extrusion)
    : extrusionPlanFootprintDimsForLenDir(extrusion, lenDir, profileInPlan);
  const meta: Record<string, string> = {};
  if (type) {
    meta.type = type;
  }
  const aabb = extrusionAABB3ByBRep(extrusion);
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType,
      id,
    },
  };
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, aabb);
  for (const parent of parents) {
    BuildingGenerationContext.indexRelate(parent, "HOSTS", entityNode);
  }
  return {
    name,
    id,
    parents,
    extrusion,
    materialSrc,
    profileInPlan,
    profileInPlanArea,
    depth: thickness,
    volume,
    entityType,
    geometryType: "EXTRUSION",
    tags,
    docEntityHandle,
    pickHandle: docEntityHandle,
    style,
    width: worldInchesNew(correctedWidth),
    length: worldInchesNew(correctedLength),
    meta,
  };
}

export function buildingAnalyticBRepNew(
  entityType: BuildingAnalyticEntityType,
  name: string,
  parents: DocEntityHandle[],
  brep: BRep,
  materialSrc: MaterialSrc | undefined,
  materialTransformations: (UVTransformation | undefined)[] | undefined,
  projectedOnPlane: Face | undefined,
  id: string,
  tags: BuildingGeomTag[] = [],
  style?: BuildingAnalyticEntityStyle,
): BuildingAnalyticBRep {
  const resolvedProjectedOnPlane = projectedOnPlane ?? bRepProjectedOnPlane(brep, planeNewXY());
  const projectedOnPlaneArea = inchesSquaredNew(faceArea(resolvedProjectedOnPlane));
  const aabb = brepAABB3(brep);
  const docEntityHandle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: {
      entityType,
      id,
    },
  };
  const entityNode = BuildingGenerationContext.indexAddNode(docEntityHandle, aabb);
  for (const parent of parents) {
    BuildingGenerationContext.indexRelate(parent, "HOSTS", entityNode);
  }
  return {
    name,
    id,
    parents,
    brep,
    materialSrc,
    materialTransformations,
    projectedOnPlane: resolvedProjectedOnPlane,
    projectedOnPlaneArea,
    entityType,
    geometryType: "BREP",
    tags,
    docEntityHandle,
    pickHandle: docEntityHandle,
    style,
  };
}

/**
 * This function generates an extrusion's "shadow" on a plane
 */
export function extrusionProfileProjectedOnPlane(extrusion: Extrusion, plane: Plane): Face {
  const mesh = extrusionToBRep(extrusion);
  return bRepProjectedOnPlane(mesh, plane);
}

export function bRepProjectedOnPlane(brep: BRep, plane: Plane): Face {
  const projected: Face[] = [];
  for (const face of brep.faces) {
    const onPlane = faceProjectCopyOnPlane(face, plane);
    if (faceArea(onPlane) === 0) {
      continue;
    }
    projected.push(onPlane);
  }
  const unioned = unionCoplanarFaces(projected);
  if (unioned.length > 1) {
    console.warn("bRepProjectedOnPlane: more than one face after unioning");
  }
  return unioned[0];
}
